import { useState, useEffect } from 'react';
import { useAuth } from "../../../../contexts/AuthContext";
import { db } from "../../../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import getServerTimestamp from '../../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../../theme';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TruCard from '../../../../components/TruCard';
import AddableList from '../../../../components/AddableList';

const dietaryOptions = {
  '': 'Select...',
  yes: 'YES - Beneficial to include',
  no: 'NO - Not beneficial',
  small: 'OK in SMALL amounts',
  less: 'Have LESS in diet',
  more: 'Add MORE into diet',
  maintain: 'Maintain what you are doing',
  none: 'Ideally NONE in diet',
  neutral: 'Not helpful or hurtful right now',
  remove: 'Ideally NONE in diet',
};

const rawCookedOptions = {
  '': 'Select...',
  both: 'Mix of Raw and Cooked',
  cooked: 'Prioritize cooked foods',
  raw: 'Prioritize raw foods',
};

export default function EditWellnessReport() {
  const params = useParams();
  const clientId = params.clientId;
  const scanId = params.scanId;
  const reportId = params.reportId;
  const isOneOff = !!reportId;  // Synchronous determination: if reportId present, it's one-off
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    wellness_pillar_priorities: [],
    limiting_factors: [],
    emotional_balance: {
      limiting_beliefs: [],
      affirmations: []
    },
    sleep_support: {
      ideal_bedtime: '',
      sleep_needed_per_night: '',
      white_noise: '',
      main_imbalance_affecting_sleep: { name: '', description: '' },
      additional_factors_affecting_sleep: [],
      restore_peptide: ''
    },
    movement_and_exercise: {
      weight_training: '',
      if_weight_training: '',
      hiit: { recommended: '', max: '' },
      workout_specifics: { intensity: '', duration: '', frequency: '' },
      workout_types: []
    },
    supportive_therapies: {
      cold_plunge: '',
      sauna_sessions: '',
      red_light_therapy: '',
      hydrotherapy: ''
    },
    nutrition_supports: {
      carb_cycling: '',
      fasting: { recommendation: '', how_often: '', how_long: '' },
      blood_sugar_balancing_support: [],
      supplement: ''
    },
    sugar_cleanse: {
      sugar_cleanse: '',
      length: '',
      fruit: ''
    },
    dietary_suggestions: {
      raw_vs_cooked_foods: '',
      animal_protein: '',
      beans: '',
      dairy: '',
      eggs: '',
      fats: '',
      fish_and_seafood: '',
      fruits: '',
      grains: '',
      gluten: '',
      nuts: '',
      vegetables: '',
      vegetables_cooked_or_raw: '',
      protein: '',
      comments: ''
    },
    foods_to_avoid: [],
    hormones: []
  });
  const [scan, setScan] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Collapsible sections state - first section starts expanded, rest collapsed to reduce scrolling on this long form
  const [expandedSections, setExpandedSections] = useState({
    wellnessPillarPriorities: true,
    limitingFactors: false,
    emotionalBalance: false,
    sleepSupport: false,
    movementAndExercise: false,
    supportiveTherapies: false,
    nutritionSupports: false,
    sugarCleanse: false,
    dietarySuggestions: false,
    foodsToAvoid: false,
    hormones: false,
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const getData = async () => {
      // Validate params before proceeding
      if (isOneOff && !reportId) {
        setError('Missing report ID for one-off edit.');
        return;
      }
      if (!isOneOff && (!clientId || !scanId)) {
        setError('Missing client or scan ID for standard edit.');
        return;
      }

      try {
        let docRef;
        if (isOneOff) {
          docRef = doc(db, "oneOffReports", reportId);
        } else {
          docRef = doc(db, "clients", clientId, "scans", scanId);
        }
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const reportData = data.reportData?.wellness || {};
          if (typeof reportData !== 'object' || reportData === null) {
            setError("This scan uses an older format and cannot be edited here.");
            navigate(isOneOff ? '/admin/reports' : `/clients/${clientId}/scans/${scanId}`);
            return;
          }
          // Normalize dropdown values to lowercase for case-insensitive matching
          const normalize = (val) => String(val || '').toLowerCase().trim();
          setFormData({
            wellness_pillar_priorities: reportData.wellness_pillar_priorities || [],
            limiting_factors: reportData.limiting_factors || [],
            emotional_balance: {
              limiting_beliefs: reportData.emotional_balance?.limiting_beliefs || [],
              affirmations: reportData.emotional_balance?.affirmations || []
            },
            sleep_support: {
              ideal_bedtime: reportData.sleep_support?.ideal_bedtime || '',
              sleep_needed_per_night: reportData.sleep_support?.sleep_needed_per_night || '',
              white_noise: reportData.sleep_support?.white_noise || '',
              main_imbalance_affecting_sleep: reportData.sleep_support?.main_imbalance_affecting_sleep || { name: '', description: '' },
              additional_factors_affecting_sleep: reportData.sleep_support?.additional_factors_affecting_sleep || [],
              restore_peptide: reportData.sleep_support?.restore_peptide || ''
            },
            movement_and_exercise: {
              weight_training: reportData.movement_and_exercise?.weight_training || '',
              if_weight_training: reportData.movement_and_exercise?.if_weight_training || '',
              hiit: reportData.movement_and_exercise?.hiit || { recommended: '', max: '' },
              workout_specifics: reportData.movement_and_exercise?.workout_specifics || { intensity: '', duration: '', frequency: '' },
              workout_types: reportData.movement_and_exercise?.workout_types || []
            },
            supportive_therapies: {
              cold_plunge: reportData.supportive_therapies?.cold_plunge || '',
              sauna_sessions: reportData.supportive_therapies?.sauna_sessions || '',
              red_light_therapy: reportData.supportive_therapies?.red_light_therapy || '',
              hydrotherapy: reportData.supportive_therapies?.hydrotherapy || ''
            },
            nutrition_supports: {
              carb_cycling: reportData.nutrition_supports?.carb_cycling || '',
              fasting: reportData.nutrition_supports?.fasting || { recommendation: '', how_often: '', how_long: '' },
              blood_sugar_balancing_support: reportData.nutrition_supports?.blood_sugar_balancing_support || [],
              supplement: reportData.nutrition_supports?.supplement || ''
            },
            sugar_cleanse: {
              sugar_cleanse: reportData.sugar_cleanse?.sugar_cleanse || '',
              length: reportData.sugar_cleanse?.length || '',
              fruit: reportData.sugar_cleanse?.fruit || ''
            },
            dietary_suggestions: {
              raw_vs_cooked_foods: normalize(reportData.dietary_suggestions?.raw_vs_cooked_foods),
              animal_protein: normalize(reportData.dietary_suggestions?.animal_protein),
              beans: normalize(reportData.dietary_suggestions?.beans),
              dairy: normalize(reportData.dietary_suggestions?.dairy),
              eggs: normalize(reportData.dietary_suggestions?.eggs),
              fats: normalize(reportData.dietary_suggestions?.fats),
              fish_and_seafood: normalize(reportData.dietary_suggestions?.fish_and_seafood),
              fruits: normalize(reportData.dietary_suggestions?.fruits),
              grains: normalize(reportData.dietary_suggestions?.grains),
              gluten: normalize(reportData.dietary_suggestions?.gluten),
              nuts: normalize(reportData.dietary_suggestions?.nuts),
              vegetables: normalize(reportData.dietary_suggestions?.vegetables),
              vegetables_cooked_or_raw: normalize(reportData.dietary_suggestions?.vegetables_cooked_or_raw),
              protein: normalize(reportData.dietary_suggestions?.protein),
              comments: reportData.dietary_suggestions?.comments || ''
            },
            foods_to_avoid: reportData.foods_to_avoid || [],
            hormones: reportData.hormones || []
          });
          setScan(data);
        } else {
          setError("Data not found.");
        }
      } catch (err) {
        setError("Failed to fetch: " + err.message);
      }
    };
    getData();
  }, [clientId, scanId, reportId, isOneOff, navigate]);

  const handleTextChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNestedTextChange = (parent, child) => (e) => {
    setFormData({
      ...formData,
      [parent]: { ...formData[parent], [child]: e.target.value }
    });
  };

  const handleDoubleNestedTextChange = (parent, child, subchild) => (e) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [child]: { ...formData[parent][child], [subchild]: e.target.value }
      }
    });
  };

  const addItem = (field, value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const updateItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addToNestedArray = (parent, field, value) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: [...formData[parent][field], value]
      }
    });
  };

  const removeFromNestedArray = (parent, field, index) => {
    const updated = formData[parent][field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const updateNestedArrayItem = (parent, field, index, value) => {
    const updated = [...formData[parent][field]];
    updated[index] = value;
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const addToNestedObjectArray = (parent, field) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: [...formData[parent][field], { name: '', description: '' }]
      }
    });
  };

  const removeFromNestedObjectArray = (parent, field, index) => {
    const updated = formData[parent][field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const updateNestedObjectArray = (parent, field, index, key, value) => {
    const updatedArray = [...formData[parent][field]];
    updatedArray[index] = { ...updatedArray[index], [key]: value };
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updatedArray
      }
    });
  };

  const handleAddFoodToAvoid = (value) => {
    if (formData.foods_to_avoid.length < 8) {
      addItem('foods_to_avoid', value);
    } else {
      // Optionally, set an error message here if needed
      console.warn('Maximum of 8 items allowed for Foods to Avoid.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();
      const updatedData = { ...formData };

      let docRef;
      if (isOneOff) {
        docRef = doc(db, "oneOffReports", reportId);
      } else {
        docRef = doc(db, "clients", clientId, "scans", scanId);
      }
      await updateDoc(docRef, {
        "reportData.wellness": updatedData,
        dateModified: timestamp,
      });

      navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      setError("Failed to update wellness report: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div style={theme.Container.basicWide}>
      <div style={theme.Heading}>EDIT WELLNESS REPORT</div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TruCard 
            title={'Wellness Pillar Priorities'}
            collapsible={true}
            expanded={expandedSections.wellnessPillarPriorities}
            onToggle={() => toggleSection('wellnessPillarPriorities')}
          >
            <AddableList
              label="Add Priority"
              items={formData.wellness_pillar_priorities}
              onAdd={(value) => addItem('wellness_pillar_priorities', value)}
              onRemove={(index) => removeItem('wellness_pillar_priorities', index)}
              onUpdate={(index, value) => updateItem('wellness_pillar_priorities', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          <TruCard 
            title={'Limiting Factors'}
            collapsible={true}
            expanded={expandedSections.limitingFactors}
            onToggle={() => toggleSection('limitingFactors')}
          >
            <AddableList
              label="Add Factor"
              items={formData.limiting_factors}
              onAdd={(value) => addItem('limiting_factors', value)}
              onRemove={(index) => removeItem('limiting_factors', index)}
              onUpdate={(index, value) => updateItem('limiting_factors', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Emotional Balance'}
            collapsible={true}
            expanded={expandedSections.emotionalBalance}
            onToggle={() => toggleSection('emotionalBalance')}
          >
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Limiting Belief"
                items={formData.emotional_balance.limiting_beliefs}
                onAdd={(value) => addToNestedArray('emotional_balance', 'limiting_beliefs', value)}
                onRemove={(index) => removeFromNestedArray('emotional_balance', 'limiting_beliefs', index)}
                onUpdate={(index, value) => updateNestedArrayItem('emotional_balance', 'limiting_beliefs', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
              />
            </Box>
            <AddableList
              label="Add Affirmation"
              items={formData.emotional_balance.affirmations}
              onAdd={(value) => addToNestedArray('emotional_balance', 'affirmations', value)}
              onRemove={(index) => removeFromNestedArray('emotional_balance', 'affirmations', index)}
              onUpdate={(index, value) => updateNestedArrayItem('emotional_balance', 'affirmations', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Sleep Support'}
            collapsible={true}
            expanded={expandedSections.sleepSupport}
            onToggle={() => toggleSection('sleepSupport')}
          >
            <TextField
              label="Ideal Bedtime"
              fullWidth
              value={formData.sleep_support.ideal_bedtime}
              onChange={handleNestedTextChange('sleep_support', 'ideal_bedtime')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Sleep Needed Per Night"
              fullWidth
              value={formData.sleep_support.sleep_needed_per_night}
              onChange={handleNestedTextChange('sleep_support', 'sleep_needed_per_night')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="White Noise"
              fullWidth
              value={formData.sleep_support.white_noise}
              onChange={handleNestedTextChange('sleep_support', 'white_noise')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Restore Peptide"
              fullWidth
              value={formData.sleep_support.restore_peptide}
              onChange={handleNestedTextChange('sleep_support', 'restore_peptide')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Main Imbalance Name"
                fullWidth
                value={formData.sleep_support.main_imbalance_affecting_sleep.name}
                onChange={handleDoubleNestedTextChange('sleep_support', 'main_imbalance_affecting_sleep', 'name')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Main Imbalance Description"
                fullWidth
                multiline
                rows={2}
                value={formData.sleep_support.main_imbalance_affecting_sleep.description}
                onChange={handleDoubleNestedTextChange('sleep_support', 'main_imbalance_affecting_sleep', 'description')}
                disabled={loading}
              />
            </Box>
            <Box>
              {formData.sleep_support.additional_factors_affecting_sleep.map((factor, index) => (
                <Box key={index} sx={{ mb: 2, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                  <TextField
                    label={`Factor ${index + 1} Name`}
                    fullWidth
                    value={factor.name}
                    onChange={(e) => updateNestedObjectArray('sleep_support', 'additional_factors_affecting_sleep', index, 'name', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label={`Factor ${index + 1} Description`}
                    fullWidth
                    multiline
                    rows={2}
                    value={factor.description}
                    onChange={(e) => updateNestedObjectArray('sleep_support', 'additional_factors_affecting_sleep', index, 'description', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 1 }}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeFromNestedObjectArray('sleep_support', 'additional_factors_affecting_sleep', index)}
                    disabled={loading}
                  >
                    Remove Factor
                  </Button>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => addToNestedObjectArray('sleep_support', 'additional_factors_affecting_sleep')}
                disabled={loading}
              >
                Add Additional Factor
              </Button>
            </Box>
          </TruCard>

          <TruCard 
            title={'Movement and Exercise'}
            collapsible={true}
            expanded={expandedSections.movementAndExercise}
            onToggle={() => toggleSection('movementAndExercise')}
          >
            <TextField
              label="Weight Training"
              fullWidth
              value={formData.movement_and_exercise.weight_training}
              onChange={handleNestedTextChange('movement_and_exercise', 'weight_training')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="If Weight Training"
              fullWidth
              value={formData.movement_and_exercise.if_weight_training}
              onChange={handleNestedTextChange('movement_and_exercise', 'if_weight_training')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <TextField
                label="HIIT Recommended"
                fullWidth
                value={formData.movement_and_exercise.hiit.recommended}
                onChange={handleDoubleNestedTextChange('movement_and_exercise', 'hiit', 'recommended')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="HIIT Max"
                fullWidth
                value={formData.movement_and_exercise.hiit.max}
                onChange={handleDoubleNestedTextChange('movement_and_exercise', 'hiit', 'max')}
                disabled={loading}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Workout Intensity"
                fullWidth
                value={formData.movement_and_exercise.workout_specifics.intensity}
                onChange={handleDoubleNestedTextChange('movement_and_exercise', 'workout_specifics', 'intensity')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Workout Duration"
                fullWidth
                value={formData.movement_and_exercise.workout_specifics.duration}
                onChange={handleDoubleNestedTextChange('movement_and_exercise', 'workout_specifics', 'duration')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Workout Frequency"
                fullWidth
                value={formData.movement_and_exercise.workout_specifics.frequency}
                onChange={handleDoubleNestedTextChange('movement_and_exercise', 'workout_specifics', 'frequency')}
                disabled={loading}
              />
            </Box>
            <AddableList
              label="Add Workout Type"
              items={formData.movement_and_exercise.workout_types}
              onAdd={(value) => addToNestedArray('movement_and_exercise', 'workout_types', value)}
              onRemove={(index) => removeFromNestedArray('movement_and_exercise', 'workout_types', index)}
              onUpdate={(index, value) => updateNestedArrayItem('movement_and_exercise', 'workout_types', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          <TruCard 
            title={'Supportive Therapies'}
            collapsible={true}
            expanded={expandedSections.supportiveTherapies}
            onToggle={() => toggleSection('supportiveTherapies')}
          >
            <TextField
              label="Cold Plunge"
              fullWidth
              value={formData.supportive_therapies.cold_plunge}
              onChange={handleNestedTextChange('supportive_therapies', 'cold_plunge')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Sauna Sessions"
              fullWidth
              value={formData.supportive_therapies.sauna_sessions}
              onChange={handleNestedTextChange('supportive_therapies', 'sauna_sessions')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Red Light Therapy"
              fullWidth
              value={formData.supportive_therapies.red_light_therapy}
              onChange={handleNestedTextChange('supportive_therapies', 'red_light_therapy')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Hydrotherapy"
              fullWidth
              value={formData.supportive_therapies.hydrotherapy}
              onChange={handleNestedTextChange('supportive_therapies', 'hydrotherapy')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Nutrition Supports'}
            collapsible={true}
            expanded={expandedSections.nutritionSupports}
            onToggle={() => toggleSection('nutritionSupports')}
          >
            <TextField
              label="Carb Cycling"
              fullWidth
              value={formData.nutrition_supports.carb_cycling}
              onChange={handleNestedTextChange('nutrition_supports', 'carb_cycling')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Fasting Recommendation"
                fullWidth
                value={formData.nutrition_supports.fasting.recommendation}
                onChange={handleDoubleNestedTextChange('nutrition_supports', 'fasting', 'recommendation')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Fasting How Often"
                fullWidth
                value={formData.nutrition_supports.fasting.how_often}
                onChange={handleDoubleNestedTextChange('nutrition_supports', 'fasting', 'how_often')}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Fasting How Long"
                fullWidth
                value={formData.nutrition_supports.fasting.how_long}
                onChange={handleDoubleNestedTextChange('nutrition_supports', 'fasting', 'how_long')}
                disabled={loading}
              />
            </Box>
            <AddableList
              label="Add Blood Sugar Balancing Support"
              items={formData.nutrition_supports.blood_sugar_balancing_support}
              onAdd={(value) => addToNestedArray('nutrition_supports', 'blood_sugar_balancing_support', value)}
              onRemove={(index) => removeFromNestedArray('nutrition_supports', 'blood_sugar_balancing_support', index)}
              onUpdate={(index, value) => updateNestedArrayItem('nutrition_supports', 'blood_sugar_balancing_support', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
            <TextField
              label="Supplement"
              fullWidth
              multiline
              rows={2}
              value={formData.nutrition_supports.supplement}
              onChange={handleNestedTextChange('nutrition_supports', 'supplement')}
              disabled={loading}
              sx={{ mt: 2 }}
            />
          </TruCard>

          <TruCard 
            title={'Sugar Cleanse'}
            collapsible={true}
            expanded={expandedSections.sugarCleanse}
            onToggle={() => toggleSection('sugarCleanse')}
          >
            <TextField
              label="Sugar Cleanse"
              fullWidth
              value={formData.sugar_cleanse.sugar_cleanse}
              onChange={handleNestedTextChange('sugar_cleanse', 'sugar_cleanse')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Length"
              fullWidth
              value={formData.sugar_cleanse.length}
              onChange={handleNestedTextChange('sugar_cleanse', 'length')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Fruit"
              fullWidth
              value={formData.sugar_cleanse.fruit}
              onChange={handleNestedTextChange('sugar_cleanse', 'fruit')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Dietary Suggestions'}
            collapsible={true}
            expanded={expandedSections.dietarySuggestions}
            onToggle={() => toggleSection('dietarySuggestions')}
          >
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Animal Protein</InputLabel>
              <Select
                value={formData.dietary_suggestions.animal_protein}
                onChange={handleNestedTextChange('dietary_suggestions', 'animal_protein')}
                label="Animal Protein"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Beans</InputLabel>
              <Select
                value={formData.dietary_suggestions.beans}
                onChange={handleNestedTextChange('dietary_suggestions', 'beans')}
                label="Beans"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Dairy</InputLabel>
              <Select
                value={formData.dietary_suggestions.dairy}
                onChange={handleNestedTextChange('dietary_suggestions', 'dairy')}
                label="Dairy"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Eggs</InputLabel>
              <Select
                value={formData.dietary_suggestions.eggs}
                onChange={handleNestedTextChange('dietary_suggestions', 'eggs')}
                label="Eggs"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Fats</InputLabel>
              <Select
                value={formData.dietary_suggestions.fats}
                onChange={handleNestedTextChange('dietary_suggestions', 'fats')}
                label="Fats"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Fish and Seafood</InputLabel>
              <Select
                value={formData.dietary_suggestions.fish_and_seafood}
                onChange={handleNestedTextChange('dietary_suggestions', 'fish_and_seafood')}
                label="Fish and Seafood"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Fruits</InputLabel>
              <Select
                value={formData.dietary_suggestions.fruits}
                onChange={handleNestedTextChange('dietary_suggestions', 'fruits')}
                label="Fruits"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Grains</InputLabel>
              <Select
                value={formData.dietary_suggestions.grains}
                onChange={handleNestedTextChange('dietary_suggestions', 'grains')}
                label="Grains"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Gluten</InputLabel>
              <Select
                value={formData.dietary_suggestions.gluten}
                onChange={handleNestedTextChange('dietary_suggestions', 'gluten')}
                label="Gluten"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Nuts</InputLabel>
              <Select
                value={formData.dietary_suggestions.nuts}
                onChange={handleNestedTextChange('dietary_suggestions', 'nuts')}
                label="Nuts"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Protein</InputLabel>
              <Select
                value={formData.dietary_suggestions.protein}
                onChange={handleNestedTextChange('dietary_suggestions', 'protein')}
                label="Protein"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Vegetables</InputLabel>
              <Select
                value={formData.dietary_suggestions.vegetables}
                onChange={handleNestedTextChange('dietary_suggestions', 'vegetables')}
                label="Vegetables"
                disabled={loading}
              >
                {Object.entries(dietaryOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Vegetables Cooked or Raw</InputLabel>
              <Select
                value={formData.dietary_suggestions.vegetables_cooked_or_raw}
                onChange={handleNestedTextChange('dietary_suggestions', 'vegetables_cooked_or_raw')}
                label="Vegetables Cooked or Raw"
                disabled={loading}
              >
                {Object.entries(rawCookedOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Raw vs Cooked Foods</InputLabel>
              <Select
                value={formData.dietary_suggestions.raw_vs_cooked_foods}
                onChange={handleNestedTextChange('dietary_suggestions', 'raw_vs_cooked_foods')}
                label="Raw vs Cooked Foods"
                disabled={loading}
              >
                {Object.entries(rawCookedOptions).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Comments"
              fullWidth
              multiline
              rows={4}
              value={formData.dietary_suggestions.comments}
              onChange={handleNestedTextChange('dietary_suggestions', 'comments')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Foods to Avoid for Weight Loss'}
            collapsible={true}
            expanded={expandedSections.foodsToAvoid}
            onToggle={() => toggleSection('foodsToAvoid')}
          >
            <AddableList
              label="Add Food"
              items={formData.foods_to_avoid}
              onAdd={handleAddFoodToAvoid}
              onRemove={(index) => removeItem('foods_to_avoid', index)}
              onUpdate={(index, value) => updateItem('foods_to_avoid', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Hormones'}
            collapsible={true}
            expanded={expandedSections.hormones}
            onToggle={() => toggleSection('hormones')}
          >
            <AddableList
              label="Add Hormone"
              items={formData.hormones}
              onAdd={(value) => addItem('hormones', value)}
              onRemove={(index) => removeItem('hormones', index)}
              onUpdate={(index, value) => updateItem('hormones', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  );
}